import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../BrandOnBoarding/api";
import { AuthContext } from "../../context/auth-context";
import { CategoryAnalysisContext } from "./category-analysis-context";

export default function useCAHistory() {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const abortCtrlRef = useRef();
  const caCtx = useContext(CategoryAnalysisContext);
  const { user, selectedCustomerId } = useContext(AuthContext);
  const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;

  useEffect(() => {
    if (abortCtrlRef.current) abortCtrlRef.current.abort();
    setHistory([]);
    setHasReachedEnd(false);
    setTotalCount(0);
    setTotalPages(0);
  }, []);

  async function getHistory(page = 1, limit = 5, searchQuery = "") {
    try {
      if (page === 1) {
        caCtx.updateTransactionHistory({ clear: true });
        setHistory([]);
      }
      setError("");
      setLoading(true);

      if (abortCtrlRef.current) abortCtrlRef.current.abort();
      const abortCtrl = new AbortController();
      abortCtrlRef.current = abortCtrl;

      const params = { page, limit, industry_customer_id: selectedCustomerId };
      if (searchQuery?.trim()) params.search = searchQuery.trim();

      const response = await api.get(
        `${webSocketBaseURL}/getUserIdHistory/categoryAnalysis/brand/${user?.user_role?.user_id}`,
        { params, signal: abortCtrl.signal }
      );

      setLoading(false);

      if (response.status !== 200) {
        setError("Failed to fetch category analysis history");
        return;
      }

      let data, count, pages;
      if (response.data && typeof response.data === "object" && !Array.isArray(response.data)) {
        data = response.data.data || response.data.records || [];
        count = response.data.totalCount || response.data.total || 0;
        pages = response.data.totalPages || Math.ceil(count / limit) || 0;
      } else {
        data = response.data || [];
        count = data.length;
        pages = Math.ceil(count / limit);
      }

      setTotalCount(count);
      setTotalPages(pages);

      if (data.length === 0 || data.length < limit) setHasReachedEnd(true);

      caCtx.updateTransactionHistory(data);
    } catch (error) {
      if (error.name === "CanceledError") return;
      setError("Failed to fetch category analysis history");
      setLoading(false);
    }
  }

  async function deleteHistory(id) {
    try {
      const response = await api.delete(`${webSocketBaseURL}/${id}?user_id=${user?.user_role?.user_id}`);
      if (response.status === 200) {
        caCtx.updateTransactionHistory({
          replace: true,
          data: caCtx.state.transactionHistory.filter((h) => h.transactionId !== id),
        });
        setTotalCount((prev) => Math.max(0, prev - 1));
      } else {
        toast.error("Failed to delete record");
      }
    } catch {
      toast.error("Failed to delete record");
    }
  }

  return {
    loading,
    error,
    history,
    setHistory,
    getHistory,
    deleteHistory,
    hasReachedEnd,
    setHasReachedEnd,
    totalCount,
    totalPages,
  };
}
